import { lazy, ComponentType } from "react";

const RELOAD_KEY = "dextoro_chunk_reload";
const RELOAD_TTL_MS = 30_000; // 30 seconds

/** Known error patterns from stale chunk loads after deployments */
const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "is not a valid JavaScript MIME type",
  "Loading chunk",
  "Loading CSS chunk",
  // Mobile Safari throws SyntaxError when a chunk partially loads due to network drop
  "Unexpected EOF",
  "Unexpected end of script",
  // Chunk loads but module is corrupted/empty (no default export)
  "Cannot read properties of undefined",
  "is not an object (evaluating",
  "Module default export missing",
] as const;

/** Returns true if the error looks like a stale chunk load failure */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : String(error ?? "");
  return CHUNK_ERROR_PATTERNS.some((pattern) =>
    message.toLowerCase().includes(pattern.toLowerCase()),
  );
}

/** Returns true if a reload was triggered within the TTL window */
export function hasRecentReload(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Date.now() - ts < RELOAD_TTL_MS;
  } catch {
    return false;
  }
}

/** Clears the reload flag so subsequent chunk errors can trigger a fresh reload */
export function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // sessionStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Drop-in replacement for React.lazy() that auto-reloads once on stale chunk
 * errors (e.g. after a deployment). Uses a timestamped sessionStorage flag to
 * prevent infinite reload loops.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory()
      .then((module) => {
        // Guard against partial chunk loads that resolve with { default: undefined }.
        // React.lazy would otherwise throw "Cannot read properties of undefined (reading 'default')"
        // during render, bypassing the catch below. See DEXTORO-LAUNCH-AS (7.6k events from 2 users).
        if (!module || typeof (module as { default?: unknown }).default !== "function") {
          throw new Error("Module default export missing, likely partial chunk load");
        }
        return module;
      })
      .catch((error: unknown) => {
        if (!isChunkLoadError(error)) throw error;

        if (!hasRecentReload()) {
          // First failure — reload to get fresh assets
          try {
            sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          } catch {
            // best-effort
          }
          window.location.reload();
          // Return a never-resolving promise so React doesn't try to render
          // the broken module while the page is reloading.
          return new Promise<never>(() => {});
        }

        // Already reloaded recently — clear flag and surface the error
        clearReloadFlag();
        throw error;
      }),
  );
}
